<template>
  <v-dialog
    v-model="show"
    persistent
    width="960px"
  >
    <v-card>
      <v-toolbar
        color="primaryLight"
        dark
        dense
      >
        <v-toolbar-title>Medios</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-text-field
          v-model="search"
          class="mt-2"
          label="Buscar"
          append-icon="mdi-magnify"
          style="width: 200px"
        />
        <v-data-table
          v-model="mediosSelected"
          :headers="headers"
          :items="mediosOrder"
          item-key="ID_MEDIO"
          :search="search"
          height="400px"
          show-select
          fixed-header
          disable-pagination
          hide-default-footer
        >
          <template
            #no-data
          >
            <p>Datos no disponibles.</p>
          </template>

          <template
            #[`header.data-table-select`]
          >
            <!-- Quitamos el boton de seleccionar todos -->
          </template>

          <template
            #[`item.data-table-select`]="{ item, isSelected, select }"
          >
            <v-simple-checkbox
              v-if="!disabledPorFinDescanso(item)"
              :value="isSelected"
              :disabled="item.ESTADO == 1 || item.ESTADO == 2"
              :ripple="false"
              @input="select($event)"
            />
            <v-tooltip
              v-else
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-icon
                  v-bind="attrs"
                  color="#c00d0d"
                  v-on="on"
                >
                  mdi-sleep
                </v-icon>
              </template>
              <span>Descanso hasta: {{ $date.formatDate(item.FECHA_FIN_DESCANSO, 'HH:mm DD/MM/YYYY') }}</span>
            </v-tooltip>
          </template>

          <template
            #[`item.MEDIO`]="{ item }"
          >
            <v-tooltip
              v-if="(item.ESTADO == 1 || item.ESTADO == 2) && item.ID_INCENDIO"
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <span
                  v-bind="attrs"
                  v-on="on"
                >{{ item.MEDIO }}</span>
              </template>
              <span>Asignado a incendio: {{ $store.getters['incendio/getIncendioByID'](item.ID_INCENDIO)?.MUNICIPIO }}</span>
            </v-tooltip>
            <span
              v-else
            >{{ item.MEDIO }}</span>
          </template>

          <template
            #[`item.EMERGENCIA`]="{ item }"
          >
            <span
              v-if="item.ID_INCENDIO"
            >
              {{ $store.getters['incendio/getIncendioByID'](item.ID_INCENDIO)?.MUNICIPIO || item.ID_INCENDIO }}
            </span>
            <span
              v-else
              class="grey--text"
            >—</span>
          </template>
        </v-data-table>

        <v-container
          fluid
        >
          <v-form
            v-model="isValid"
          >
            <v-row>
              <v-col>
                <vx-date-text-edit
                  v-model="fechaAviso"
                  label="Fecha aviso"
                  icon="esri-icon-calendar"
                  :min="dateMin"
                  not-future
                  required
                />
              </v-col>
              <v-col>
                <v-select
                  v-model="idSector"
                  :items="sectores"
                  label="Sector"
                  prepend-icon="esri-icon-description"
                  item-text="SECTOR"
                  item-value="ID_SECTOR"
                  :rules="[globalRules.required]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="red"
          @click="cancelar"
        >
          Cancelar
        </v-btn>
        <v-btn
          text
          color="green"
          :disabled="disableAdd"
          @click="aceptar"
        >
          Aceptar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import constants, { MEDIO_CATEGORIA } from '@/helpers/constants'

export default {
  props: {
    show: Boolean,
    idIncendio: String
  },

  data: () => ({
    isValid: false,

    search: '',
    headers: [
      { text: 'Nombre', value: 'MEDIO', align: 'left', sortable: true },
      { text: 'Categoría', value: 'CATEGORIA', align: 'left', sortable: true },
      { text: 'Tipo', value: 'TIPO', align: 'left', sortable: true },
      { text: 'Base', value: 'INFRAESTRUCTURA', align: 'left', sortable: true },
      { text: 'Entrada', value: 'HORA_ENTRADA', align: 'left', sortable: true },
      { text: 'Salida', value: 'HORA_SALIDA', align: 'left', sortable: true },
      { text: 'Emergencia actual', value: 'EMERGENCIA', align: 'left', sortable: false }
    ],

    fechaAviso: null,
    dateMin: null,

    mediosSelected: [],

    idSector: null,

    mediosOrder: []
  }),

  computed: {
    disableAdd() {
      return this.mediosSelected.length === 0 || !this.isValid
    },

    incendio() {
      return this.$store.getters['incendio/getIncendioByID'](this.idIncendio)
    },

    medios() {
      const medios = JSON.parse(JSON.stringify(this.$store.getters['medio/medios'] || []))
      const inc = this.incendio

      medios.forEach((m) => {
        let fechaDescanso = this.$date.parseDate('2018-01-01 00:00:00.000')
        inc?.SECTORES.forEach((sector) => {
          sector?.MEDIOS.forEach((medioSector) => {
            if (medioSector?.ID_MEDIO?.equalsIgnoreCase?.(m.ID_MEDIO) && medioSector.FECHA_FIN_DESCANSO) {
              const newFechaDescanso = this.$date.parseDate(medioSector.FECHA_FIN_DESCANSO, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
              if (newFechaDescanso > fechaDescanso) fechaDescanso = newFechaDescanso
            }
          })
        })

        if (fechaDescanso.format('YYYY/MM/DD HH:mm') !== '2018/01/01 00:00') {
          m.FECHA_FIN_DESCANSO = fechaDescanso
        }
      })

      return medios
    },

    sectores() {
      let sectores = []

      if (this.incendio) {
        sectores = this.incendio.SECTORES
      }

      return sectores
    },

    isMedioAereo() {
      return this.mediosSelected.length > 0 && this.mediosSelected.every(medio => medio.ID_MEDIO_CATEGORIA === MEDIO_CATEGORIA.AEREO)
    }
  },

  watch: {
    show() {
      if (this.show) {
        this.fechaAviso = this.$date.currentDate()
        this.idSector = this.sectores[0].ID_SECTOR

        this.dateMin = this.$date.parseDate(this.incendio.FECHA_CREACION)

        this.orderMedios()
      }
    },

    medios() {
      this.orderMedios()
    }
  },

  methods: {
    disabledPorFinDescanso(medio) {
      let disabled = false

      if (medio.FECHA_FIN_DESCANSO) {
        const now = this.$date.now()
        const fechaFinDescanso = this.$date.parseDate(medio.FECHA_FIN_DESCANSO)
        disabled = fechaFinDescanso > now
      }
      return disabled
    },

    orderMedios() {
      const mediosOrder = JSON.parse(JSON.stringify(this.medios))
      /* for (let i = 0; i < mediosOrder.length; i++) { // TODO: revisar que es esto
        let m = mediosOrder[i]
        if (m.ESTADO === 3) {
          m.ESTADO = 0
        }
      } */

      mediosOrder.sort((a, b) => {
        const aEstado = a.ESTADO
        const bEstado = b.ESTADO
        const aMedio = a.MEDIO.toUpperCase()
        const bMedio = b.MEDIO.toUpperCase()

        if (aEstado === bEstado) {
          return (aMedio < bMedio) ? -1 : (aMedio > bMedio) ? 1 : 0
        } else {
          return (aEstado < bEstado) ? -1 : 1
        }
      })

      this.mediosOrder = mediosOrder
    },

    cancelar() {
      this.$emit('cancelar')
      this.mediosSelected = []
    },

    aceptar() {
      const mediosIncendio = {
        ID_INCENDIO: this.idIncendio,
        ID_SECTOR: this.idSector,
        MEDIOS: []
      }

      for (let i = 0; i < this.mediosSelected.length; i++) {
        const idMedioSector = this.$uuid.createUUID()

        let tiempos = {
          tiempoJornada: this.$store.getters['parametro/getParametro']('DEFAULT_TIEMPO_ACTUACION'),
          tiempoDescanso: this.$store.getters['parametro/getParametro']('DEFAULT_TIEMPO_DESCANSO')
        }
        if (this.mediosSelected[i].TIPO === 'Retén Tragsa' || this.mediosSelected[i].TIPO === 'Cuadrilla Helitransportada') {
          tiempos = constants.tiemposTrabajoMediosReducido
        }

        const fechaAviso = this.$date.parseDate(this.fechaAviso)

        const inicioJornada = this.$date.parseDate(`${fechaAviso.format('DD/MM/YYYY')} ${this.mediosSelected[i].HORA_ENTRADA}`) // TODO: Y que pasa si el inicio jornada fue el dia antes?
        const planificacion = {
          ID_PLANIFICACION_MEDIO: this.$uuid.createUUID(),
          ID_MEDIO_SECTOR: idMedioSector,
          INICIO_JORNADA: inicioJornada,
          TIEMPO_OPERATIVO: tiempos.tiempoJornada,
          TIEMPO_DESCANSO: tiempos.tiempoDescanso
        }

        const medioSector = {
          ID_MEDIO_SECTOR: idMedioSector,
          ID_SECTOR: this.idSector,
          ID_MEDIO: this.mediosSelected[i].ID_MEDIO,
          FECHA_ALARMADO: fechaAviso,
          PLANIFICACION: planificacion,
          CREATE_MMAA_PLANIF: this.isMedioAereo
        }
        mediosIncendio.MEDIOS.push(medioSector)
      }

      this.$emit('aceptar', mediosIncendio)
      this.mediosSelected = []
    }
  }
}
</script>

<style scoped>
</style>
